import _, { sortBy } from 'lodash';
import gql from 'graphql-tag';
import {
  OrderDiscountOffer,
  BundleOffer,
  StagedBundleOffer,
  StagedIndividualOffer,
  NewDiscountType,
  DiscountedOffer,
} from '@baemingo/offer';
import { DISCOUNT_TYPE, OFFER_TYPE } from '~../__queries__/globalTypes';

import { LocalEntry, i18nProps } from '~Types';

import {
  CompanyFragment,
  VenueFragment,
  offerCompany,
  offerCompany_offers,
  offerVenue,
} from './__queries__';
import { ApolloClient } from '@apollo/client';

interface Group {
  offerId: string;
  entries: LocalEntry[];
}

type ExtendedDiscountedOffer = DiscountedOffer & { discountGroupId: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function calculateOfferValues(groups: Group[], venueId: string, client: ApolloClient<any>) {
  let company: CompanyFragment | null = null;

  try {
    const venue = client.readFragment<VenueFragment>({
      id: venueId,
      fragment: gql`
        fragment VenueFragment on Venue {
          id

          company {
            id
          }
        }
      `,
    })!;

    // This fails during boot. Its ok.
    company = client.readFragment<CompanyFragment>({
      id: venue.company.id,
      fragment: gql`
        fragment CompanyFragment on Company {
          id
          offers(context: [POS]) {
            id
            type

            nameLang {
              id
              se
              en
              es
              no
            }

            discountType
            discountRate
            overrideItemPrices

            items(filter: { deleted: false }) {
              id
              titleLang {
                id
                se
                en
                es
                no
              }
            }

            categories {
              id
            }

            stages {
              id
              discountRate
              discountType

              title {
                id
                se
                en
                es
                no
              }

              items(filter: { deleted: false }) {
                id
              }
            }
          }
        }
      `,
    });
  } catch (e) {
    // Do nothing
  }

  if (!company) {
    return null;
  }

  const { offers } = company;

  // Map old DISCOUNT_TYPE to new NewDiscountType
  const discountTypeMap: Record<DISCOUNT_TYPE, NewDiscountType> = {
    [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
    [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
    [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
  };

  const prices = groups.map((group) => {
    const rawOffer = offers.find((o) => o.id === group.offerId)!;
    const { stages, type, discountRate, discountType } = rawOffer;

    // Handle staged offer types using the new API
    if (type === OFFER_TYPE.STAGED_BUNDLE && stages.length > 0) {
      // Use new StagedBundleOffer for STAGED_BUNDLE
      const offer = new StagedBundleOffer({
        discountType: discountTypeMap[discountType],
        discountRate: discountRate,
        overrideItemPrices: rawOffer.overrideItemPrices,
      });

      // Add items to the offer
      group.entries.forEach((e) => {
        offer.addItem({
          id: e.id,
          itemId: e.item.id,
          categoryId: e.item.categoryId,
          amount: e.price,
        });
      });

      // Add stages - match cart entries to stages by menu item ID
      // Each stage has an items array with menu item IDs
      stages.forEach((stage) => {
        const stageItemIds = stage.items.map((i) => i.id);

        // Find cart entries that match this stage's menu item IDs
        const matchingEntries = group.entries.filter((e) => stageItemIds.includes(e.item.id));

        if (matchingEntries.length > 0) {
          offer.addStage({
            id: stage.id,
            itemIds: matchingEntries.map((e) => e.id), // Cart entry IDs
          });
        }
      });

      const { changedItems } = offer.calculate();

      // Convert to DiscountedOffer format
      return changedItems.map((item) => ({
        id: item.id,
        originalPrice: item.amount,
        discountedPrice: item.amount - item.discount,
        vatRate: 0, // VAT rate handling is simplified in new API
        discountGroupId: rawOffer.id, // Include offer ID for reference
      }));
    }

    if (type === OFFER_TYPE.STAGED_INDIVIDUAL && stages.length > 0) {
      // Use new StagedIndividualOffer for STAGED_INDIVIDUAL
      const offer = new StagedIndividualOffer();

      // Add items to the offer
      group.entries.forEach((e) => {
        offer.addItem({
          id: e.id,
          itemId: e.item.id,
          categoryId: e.item.categoryId,
          amount: e.price,
        });
      });

      // Add discounted stages - match cart entries to stages by menu item ID
      // Each stage has its own discount rate and items array with menu item IDs
      stages.forEach((stage) => {
        const stageItemIds = stage.items.map((i) => i.id);

        // Find cart entries that match this stage's menu item IDs
        const matchingEntries = group.entries.filter((e) => stageItemIds.includes(e.item.id));

        if (matchingEntries.length > 0) {
          offer.addDiscountedStage({
            id: stage.id,
            itemIds: matchingEntries.map((e) => e.id), // Cart entry IDs
            discountType: discountTypeMap[stage.discountType],
            discountRate: stage.discountRate,
          });
        }
      });

      const { changedItems } = offer.calculate();

      // Convert to DiscountedOffer format
      return changedItems.map((item) => ({
        id: item.id,
        originalPrice: item.amount,
        discountedPrice: item.amount - item.discount,
        vatRate: 0,
        discountGroupId: rawOffer.id, // Include offer ID for reference
      }));
    }

    // Use new API for simple offer types
    let result: ExtendedDiscountedOffer[] = [];

    switch (type) {
      case OFFER_TYPE.BUNDLE: {
        // BundleOffer requires bundleItemIds in constructor
        const bundleItemIds = group.entries.map((e) => e.item.id);
        const offer = new BundleOffer({
          discountType: discountTypeMap[discountType],
          discountRate: discountRate,
          bundleItemIds: bundleItemIds,
          overrideItemPrices: rawOffer.overrideItemPrices,
        });

        // Add items to the offer
        group.entries.forEach((e) => {
          offer.addItem({
            id: e.id,
            itemId: e.item.id,
            categoryId: e.item.categoryId,
            amount: e.price,
          });
        });

        const { changedItems } = offer.calculate();

        // Convert to DiscountedOffer format to match old API
        result = changedItems.map((item) => ({
          id: item.id,
          originalPrice: item.amount,
          discountedPrice: item.amount - item.discount,
          vatRate: 0, // VAT rate handling is simplified in new API
          discountGroupId: rawOffer.id, // Include offer ID for reference
        }));
        break;
      }

      case OFFER_TYPE.ORDER_DISCOUNT: {
        const offer = new OrderDiscountOffer({
          discountType: discountTypeMap[discountType],
          discountRate: discountRate,
          allowedCategoryIds: rawOffer.categories.map((c) => c.id),
          allowedItemIds: rawOffer.items.map((i) => i.id),
        });

        // Add items to the offer
        group.entries.forEach((e) => {
          offer.addItem({
            id: e.id,
            itemId: e.item.id,
            categoryId: e.item.categoryId,
            amount: e.price,
          });
        });

        const { changedItems } = offer.calculate();

        // Convert to DiscountedOffer format
        result = changedItems.map((item) => ({
          id: item.id,
          originalPrice: item.amount,
          discountedPrice: item.amount - item.discount,
          vatRate: 0,
          discountGroupId: rawOffer.id, // Include offer ID for reference
        }));
        break;
      }

      case OFFER_TYPE.ITEM_DISCOUNT: {
        // For ITEM_DISCOUNT, use OrderDiscountOffer as it's similar
        const offer = new OrderDiscountOffer({
          discountType: discountTypeMap[discountType],
          discountRate: discountRate,
          allowedCategoryIds: rawOffer.categories.map((c) => c.id),
          allowedItemIds: rawOffer.items.map((i) => i.id),
        });

        group.entries.forEach((e) => {
          offer.addItem({
            id: e.id,
            itemId: e.item.id,
            categoryId: e.item.categoryId,
            amount: e.price,
          });
        });

        const { changedItems } = offer.calculate();

        result = changedItems.map((item) => ({
          id: item.id,
          originalPrice: item.amount,
          discountedPrice: item.amount - item.discount,
          vatRate: 0,
          discountGroupId: rawOffer.id, // Include offer ID for reference
        }));
        break;
      }

      default:
        result = group.entries.map((e) => {
          return {
            id: e.id,
            originalPrice: e.price,
            discountedPrice: e.price,
            vatRate: e.item.vatRate,
            discountGroupId: group.offerId, // Include offer ID for reference
          };
        });
        break;
    }

    return result;
  });

  return prices;
}

export function calculateOfferTotals(groups: Group[], venueId: string, client: ApolloClient<any>) {
  const prices = calculateOfferValues(groups, venueId, client);

  // If we havent loaded all data yet
  if (!prices) {
    return [];
  }

  return groups.map((group, index) => {
    const fraction = group.entries[0].fraction;
    const discounted = prices[index];
    const amount = _.sumBy(discounted, 'originalPrice');
    const discount = amount - _.sumBy(discounted, 'discountedPrice');

    return {
      offerId: group.offerId,
      price: {
        amount,
        discount,
        fraction,
      },
    };
  });
}

// Helper function to try matching a bundle offer
const tryMatchBundle = <T extends MatchableItem>(
  offer: offerCompany_offers,
  candidateItems: T[],
): T[] | null => {
  const matched = offer.items
    .map((oItem) => candidateItems.find((aItem) => aItem.itemId === oItem.id))
    .filter(Boolean) as T[];

  return matched.length === offer.items.length ? matched : null;
};

// Helper function to find all matching candidate items per stage
const getCandidatesPerStage = <T extends MatchableItem>(
  stages: offerCompany_offers['stages'],
  candidateItems: T[],
): T[][] => {
  return stages.map((stage) =>
    candidateItems.filter((item) =>
      stage.items.some((sItem) => sItem.id === item.itemId),
    ),
  );
};

// Recursively find a combination of items (one per stage) matching a target total
const findCombinationWithTotal = <T extends MatchableItem>(
  candidatesPerStage: T[][],
  targetTotal: number,
  stageIndex: number,
  usedIds: string[],
  currentTotal: number,
  currentSelection: T[],
): T[] | null => {
  if (stageIndex === candidatesPerStage.length) {
    return currentTotal === targetTotal ? currentSelection : null;
  }

  const available = candidatesPerStage[stageIndex].filter(
    (item) => !usedIds.includes(item.id),
  );

  for (const item of available) {
    const result = findCombinationWithTotal(
      candidatesPerStage,
      targetTotal,
      stageIndex + 1,
      [...usedIds, item.id],
      currentTotal + (item.amount || 0),
      [...currentSelection, item],
    );

    if (result) return result;
  }

  return null;
};

// Helper function to try matching a staged offer
const tryMatchStaged = <T extends MatchableItem>(
  offer: offerCompany_offers,
  candidateItems: T[],
): T[] | null => {
  // When overrideItemPrices is true and discount type is FIXED,
  // prioritize a combination of items (one per stage) whose total price matches the discountRate
  if (offer.overrideItemPrices && offer.discountType === DISCOUNT_TYPE.FIXED) {
    const candidatesPerStage = getCandidatesPerStage(offer.stages, candidateItems);

    if (!candidatesPerStage.some((c) => c.length === 0)) {
      const exactMatch = findCombinationWithTotal(
        candidatesPerStage,
        offer.discountRate,
        0,
        [],
        0,
        [],
      );

      if (exactMatch) return exactMatch;
    }
  }

  // Default greedy matching: pick the first matching item per stage
  const stageMatches: T[] = [];
  const usedInThisOffer: string[] = [];

  offer.stages.forEach((stage) => {
    const availableForStage = candidateItems.filter((item) => !usedInThisOffer.includes(item.id));

    const item = _.intersectionWith(
      availableForStage,
      stage.items,
      (aItem, sItem) => aItem.itemId === sItem.id,
    )[0] as T | undefined;

    if (item) {
      stageMatches.push(item);
      usedInThisOffer.push(item.id);
    }
  });

  return stageMatches.length === offer.stages.length ? stageMatches : null;
};

// /**
//  * Groups items by matching offers (BUNDLE and STAGE types).
//  * Returns matched offer groups and unmatched items for rendering orange grouped headers.
//  */
type MatchableItem = {
  id: string;
  itemId: string;
  discountGroupId?: string | null;
  amount?: number;
};

export function groupOfferMatches<T extends MatchableItem>(
  items: T[],
  venueId: string,
  client: ApolloClient<any>,
  i18n: i18nProps,
): {
  matches: Array<{
    offerId: string;
    offerName: string;
    items: T[];
  }>;
  unmatched: T[];
} {
  let company: offerCompany | null = null;

  const unSortedItems = items;

  // We sort items by price for the most correct grouping of items in offers
  const sortedItemsByPrice = sortBy(items, (item) => item.amount);

  try {
    const venue = client.readFragment<offerVenue>({
      id: venueId,
      fragment: gql`
        fragment offerVenue on Venue {
          id
          company {
            id
          }
        }
      `,
    });

    if (venue) {
      company = client.readFragment<offerCompany>({
        id: venue.company.id,
        fragment: gql`
          fragment offerCompany on Company {
            id
            offers(context: [POS]) {
              id
              type
              enabled
              discountType
              discountRate
              overrideItemPrices

              nameLang {
                id
                se
                en
                es
                no
              }

              items(filter: { deleted: false }) {
                id
                titleLang {
                  id
                  se
                  en
                  es
                  no
                }
              }

              stages {
                id
                title {
                  id
                  se
                  en
                  es
                  no
                }

                items(filter: { deleted: false }) {
                  id
                }
              }
            }
          }
        `,
      });
    }
  } catch (e) {
    // Do nothing - cache read failed
  }

  if (!company) {
    return { unmatched: items, matches: [] };
  }

  const offers = company.offers.filter((o) => o.enabled);

  const sorted = _.sortBy(offers, (offer) => {
    if (offer.type === 'BUNDLE') {
      return offer.items.length;
    }
    return offer.stages.length;
  }).reverse();

  let usedItemIds: string[] = [];
  const matches: Array<{ offerId: string; offerName: string; items: T[] }> = [];

  // PHASE 1: Match items that already have a discountGroupId
  // This ensures items stay grouped with their assigned offers
  const itemsWithGroupId = sortedItemsByPrice.filter((item) => item.discountGroupId);
  const itemsByGroupId = _.groupBy(itemsWithGroupId, (item) => item.discountGroupId);

  // For each group, try to match with its corresponding offer
  Object.entries(itemsByGroupId).forEach(([groupId, groupItems]) => {
    const offer = offers.find((o) => o.id === groupId);
    if (!offer || !offer.enabled) {
      return;
    }

    // Keep trying to find complete matches within this group
    // until no more complete matches can be found
    let remainingGroupItems = [...groupItems];
    let foundMatch = true;
    let matchCount = 0;

    while (foundMatch && remainingGroupItems.length > 0) {
      foundMatch = false;
      let matchedItems: T[] | null = null;

      // Try to match based on offer type
      if (offer.type === 'BUNDLE' && offer.items.length > 0) {
        matchedItems = tryMatchBundle(offer, remainingGroupItems);
      } else if (offer.stages.length > 0) {
        matchedItems = tryMatchStaged(offer, remainingGroupItems);
      }

      // If we found a complete match, add it and mark items as used
      if (matchedItems) {
        matchCount++;

        usedItemIds = usedItemIds.concat(matchedItems.map((i) => i.id));
        matches.push({
          offerId: offer.id,
          offerName: i18n(offer.nameLang),
          items: matchedItems,
        });

        // Remove matched items from remaining group items
        remainingGroupItems = remainingGroupItems.filter(
          (item) => !matchedItems!.some((matched) => matched.id === item.id),
        );
        foundMatch = true;
      }
    }
  });

  // PHASE 2: Match remaining items without discountGroupId
  // Keep trying to find matches until no more matches can be made
  let match = false;

  do {
    match = false;

    sorted.forEach((offer) => {
      if (match) return;

      const availableItems = sortedItemsByPrice.filter((item) => !usedItemIds.includes(item.id));
      if (availableItems.length === 0) return;

      let matchedItems: T[] | null = null;

      // Try to match based on offer type
      if (offer.type === 'BUNDLE' && offer.items.length > 0) {
        matchedItems = tryMatchBundle(offer, availableItems);
      } else if (offer.stages.length > 0) {
        matchedItems = tryMatchStaged(offer, availableItems);
      }

      // If we found a complete match, add it and mark items as used
      if (matchedItems) {
        usedItemIds = usedItemIds.concat(matchedItems.map((i) => i.id));
        matches.push({
          offerId: offer.id,
          offerName: i18n(offer.nameLang),
          items: matchedItems,
        });
        match = true;
      }
    });
  } while (match);

  return {
    matches,
    unmatched: unSortedItems.filter((i) => !usedItemIds.includes(i.id)),
  };
}
